import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  X, Trash2, Download, Upload, CalendarRange, Repeat, Plus, Pencil,
  MapPin, Clock, Building2, FileText, AlertCircle, HelpCircle,
  Link as LinkIcon,
} from "lucide-react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import MonthCalendar from "../components/MonthCalendar";
import { useAuth } from "../context/AuthContext";
import { localYmd as ymd } from "../lib/utils";
import { toast } from "sonner";

const EVENT_TYPES = [
  "rehearsal", "concert", "teaching", "recording",
  "audition", "travel", "personal", "meeting",
  "administration", "other",
];

const IMPACT_OPTIONS = [
  { v: "unavailable", label: "Unavailable", className: "bg-terracotta-500 text-white" },
  { v: "tentative", label: "Tentative", className: "bg-amber-deep text-white" },
  { v: "available", label: "Available", className: "bg-sage-500 text-white" },
];

const IMPACT_PILL = {
  unavailable: "bg-terracotta-100 text-terracotta-700",
  tentative: "bg-amber-100 text-amber-800",
  available: "bg-sage-100 text-sage-700",
};

function prettyDate(s) {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  const isCurrentYear = d.getFullYear() === new Date().getFullYear();
  const opts = isCurrentYear
    ? { weekday: "short", day: "numeric", month: "short" }
    : { weekday: "short", day: "numeric", month: "short", year: "numeric" };
  return d.toLocaleDateString(undefined, opts);
}

function fmtRange(start, end) {
  if (!start && !end) return "All day";
  if (start && end) return `${start} – ${end}`;
  return start || end;
}

function eventTypeLabel(t) {
  if (!t) return "Event";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function sourceLabel(s) {
  if (s === "booking") return "From booking";
  if (s === "dep_broadcast") return "From dep confirmation";
  if (s === "rehearsal") return "From ensemble rehearsal";
  if (s === "migrated") return "From your diary history";
  return "";
}

export default function Diary() {
  const { user } = useAuth();
  const [view, setView] = useState(new Date());
  const [selected, setSelected] = useState(ymd(new Date()));
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("month");
  const [showEventModal, setShowEventModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showRepeat, setShowRepeat] = useState(false);
  const [showRange, setShowRange] = useState(false);

  // Pull a generous window so the calendar markers + list view both have data.
  const monthRange = useMemo(() => {
    const start = new Date(view.getFullYear(), view.getMonth() - 1, 1);
    const end = new Date(view.getFullYear(), view.getMonth() + 2, 0);
    return { start: ymd(start), end: ymd(end) };
  }, [view]);

  const refresh = async () => {
    try {
      const { data } = await api.get(
        `/diary/events?from=${monthRange.start}&to=${monthRange.end}`,
      );
      setEvents(data || []);
    } catch (e) {
      // Diary collection may be empty / endpoint not deployed → degrade gracefully.
      setEvents([]);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [monthRange.start, monthRange.end]);

  // Group events by date.
  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      (map[e.date] = map[e.date] || []).push(e);
    });
    // Sort each day's events by start_time (events without times sort first).
    Object.values(map).forEach((arr) => arr.sort((a, b) => {
      const at = a.start_time || "";
      const bt = b.start_time || "";
      return at.localeCompare(bt);
    }));
    return map;
  }, [events]);

  // Pass a simplified status map to the calendar so day cells get the right colour.
  // The calendar's existing prop is `availabilityByDate` keyed by date → list of
  // entries with `status` and `slot` — we synthesise that from diary events.
  const availabilityShim = useMemo(() => {
    const out = {};
    Object.entries(byDate).forEach(([date, list]) => {
      // Reduce to a single dominant status: unavailable > tentative > available.
      const impacts = list.map((e) => e.availability_impact);
      let status = "available";
      if (impacts.includes("unavailable")) status = "unavailable";
      else if (impacts.includes("tentative")) status = "maybe";
      out[date] = [{ status, slot: "all_day" }];
    });
    return out;
  }, [byDate]);

  const dayEvents = byDate[selected] || [];

  const [scopePrompt, setScopePrompt] = useState(null);
  // scopePrompt shape: { mode: "delete"|"edit", event, updated? }

  const openNew = () => {
    setEditing(null);
    setShowEventModal(true);
  };
  const openEdit = (ev) => {
    setEditing(ev);
    setShowEventModal(true);
  };

  const quickMarkUnavailable = async () => {
    try {
      await api.post("/diary/events", {
        title: "Unavailable",
        event_type: "other",
        organisation: "",
        venue: "",
        date: selected,
        start_time: null,
        end_time: null,
        notes: "",
        availability_impact: "unavailable",
      });
      toast.success(`${prettyDate(selected)} blocked in your diary`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const removeEvent = async (ev) => {
    // Legacy single-arg callers pass just the id; support both shapes.
    if (typeof ev === "string") {
      try {
        await api.delete(`/diary/events/${ev}`);
        refresh();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
      return;
    }
    if (ev.series_id || ev.is_recurring_instance || ev.is_series_master) {
      setScopePrompt({ mode: "delete", event: ev });
      return;
    }
    try {
      await api.delete(`/diary/events/${ev.id}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const confirmScopedDelete = async (scope) => {
    const ev = scopePrompt?.event;
    if (!ev) return;
    try {
      await api.delete(`/diary/events/${ev.id}?scope=${scope}`);
      toast.success(scope === "this" ? "Occurrence cancelled"
                    : scope === "series" ? "Series deleted"
                    : "This and future removed");
      setScopePrompt(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadIcs = async () => {
    try {
      const res = await fetch(`${API}/calendar/me.ics`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      const text = await res.text();
      const blob = new Blob([text], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "amds-diary.ics";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Diary exported (.ics)");
    } catch (e) {
      toast.error("Could not download calendar");
    }
  };

  return (
    <div className="space-y-5" data-testid="diary-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="label-overline">Your diary</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">
            Hi, {user?.name?.split(" ")[0] || "there"}.
          </h1>
          <p className="text-ink-600 text-sm mt-1">
            What you're doing, where, and when. Availability follows from your diary.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user?.role === "musician" && (
            <button onClick={() => setShowRange(true)} className="btn-secondary text-sm" data-testid="bulk-range-btn">
              <CalendarRange size={16} /> Block a range
            </button>
          )}
          <button onClick={downloadIcs} className="btn-secondary text-sm" data-testid="export-ics-btn">
            <Download size={16} /> Export .ics
          </button>
          <Link to="/diary/import" className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="import-ics-btn">
            <Upload size={16} /> Import .ics
          </Link>
        </div>
      </div>

      <div className="flex gap-1 bg-ivory-100 rounded-full p-1 w-fit">
        {[{ k: "month", l: "Month" }, { k: "list", l: "Upcoming" }].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              tab === t.k ? "bg-white shadow-soft text-ink-900" : "text-ink-600"
            }`}
            data-testid={`diary-tab-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === "month" ? (
        <div className="grid lg:grid-cols-2 gap-5">
          <MonthCalendar
            viewDate={view}
            onPrev={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
            onNext={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
            selectedDate={selected}
            onSelect={setSelected}
            availabilityByDate={availabilityShim}
          />

          <div className="card-base p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-overline">Selected</div>
                <h3 className="font-heading text-xl font-semibold">{prettyDate(selected)}</h3>
              </div>
              <div className="flex gap-2">
                {user?.role === "musician" && (
                  <button
                    onClick={() => setShowRepeat(true)}
                    className="btn-ghost text-sm"
                    data-testid="diary-repeat-btn"
                    title="Repeat weekly pattern"
                  >
                    <Repeat size={14} />
                  </button>
                )}
                <button
                  onClick={openNew}
                  className="btn-primary text-sm"
                  data-testid="diary-add-event-btn"
                >
                  <Plus size={14} /> Add event
                </button>
              </div>
            </div>

            <div className="space-y-2.5">
              {dayEvents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-ivory-300 p-4 text-center" data-testid="diary-empty">
                  <p className="text-sm text-ink-600 mb-3">
                    Nothing scheduled — add an event or block the day.
                  </p>
                  <div className="flex gap-2 justify-center flex-wrap">
                    <button onClick={openNew} className="btn-secondary text-sm" data-testid="diary-empty-add">
                      <Plus size={14} /> Add event
                    </button>
                    <button onClick={quickMarkUnavailable} className="btn-ghost text-sm" data-testid="diary-empty-mark-unavailable">
                      Block this day
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {dayEvents.map((ev) => (
                    <EventCard
                      key={ev.id}
                      ev={ev}
                      onEdit={() => openEdit(ev)}
                      onDelete={() => removeEvent(ev)}
                    />
                  ))}
                  <button
                    onClick={openNew}
                    className="w-full rounded-xl border border-dashed border-ivory-300 text-sm text-ink-600 py-2.5 hover:bg-ivory-50 transition inline-flex items-center justify-center gap-2"
                    data-testid="diary-add-more"
                  >
                    <Plus size={14} /> Add another event
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <UpcomingList byDate={byDate} />
      )}

      {showEventModal && (
        <EventModal
          initial={editing}
          defaultDate={selected}
          onClose={() => { setShowEventModal(false); setEditing(null); }}
          onSaved={() => { setShowEventModal(false); setEditing(null); refresh(); }}
        />
      )}
      {showRepeat && <RepeatModal onClose={() => { setShowRepeat(false); refresh(); }} startDate={selected} />}
      {showRange && <RangeModal onClose={() => { setShowRange(false); refresh(); }} startDate={selected} />}
      {scopePrompt && (
        <ScopeDialog
          mode={scopePrompt.mode}
          event={scopePrompt.event}
          onCancel={() => setScopePrompt(null)}
          onConfirm={scopePrompt.mode === "delete" ? confirmScopedDelete : scopePrompt.onConfirm}
        />
      )}
    </div>
  );
}


// ---------------- Event card ----------------

function EventCard({ ev, onEdit, onDelete }) {
  const impactPill = IMPACT_PILL[ev.availability_impact] || IMPACT_PILL.unavailable;
  const isAuto = ev.source_type && ev.source_type !== "manual";
  const linkable = ev.source_type === "booking" && ev.source_id ? `/bookings/${ev.source_id}` : null;
  return (
    <div className="rounded-xl border border-ivory-200 p-3.5 space-y-1.5" data-testid={`diary-event-${ev.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold text-ink-900 text-[15px] leading-tight">{ev.title || eventTypeLabel(ev.event_type)}</h4>
            <span className={`pill !text-[10px] ${impactPill}`} data-testid={`diary-event-impact-${ev.id}`}>
              {(ev.availability_impact || "unavailable").replace(/^./, (c) => c.toUpperCase())}
            </span>
            {isAuto && (
              <span className="pill bg-ivory-100 text-ink-600 !text-[10px]" data-testid={`diary-event-source-${ev.id}`}>
                {sourceLabel(ev.source_type)}
              </span>
            )}
          </div>
          {ev.organisation && (
            <div className="text-sm text-ink-700 mt-0.5 inline-flex items-center gap-1.5">
              <Building2 size={12} className="text-ink-500" /> {ev.organisation}
            </div>
          )}
          <div className="text-xs text-ink-500 mt-1 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1"><Clock size={11} /> {fmtRange(ev.start_time, ev.end_time)}</span>
            {ev.venue && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {ev.venue}</span>}
            <span className="inline-flex items-center gap-1 capitalize"><FileText size={11} /> {eventTypeLabel(ev.event_type)}</span>
          </div>
          {ev.notes && (
            <p className="text-xs text-ink-600 mt-2 whitespace-pre-wrap">{ev.notes}</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {linkable && (
            <a href={linkable} className="btn-ghost !p-1.5" title="Open booking" data-testid={`diary-event-link-${ev.id}`}>
              <LinkIcon size={14} />
            </a>
          )}
          <button onClick={onEdit} className="btn-ghost !p-1.5" title="Edit" data-testid={`diary-event-edit-${ev.id}`}>
            <Pencil size={14} />
          </button>
          <button onClick={onDelete} className="btn-ghost !p-1.5 text-terracotta-600" title="Delete" data-testid={`diary-event-delete-${ev.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------- Upcoming list view ----------------

function UpcomingList({ byDate }) {
  const [showPast, setShowPast] = useState(false);
  const today = ymd(new Date());
  const upcomingDays = Object.keys(byDate).filter((d) => d >= today).sort();
  const pastDays = Object.keys(byDate).filter((d) => d < today).sort().reverse();
  const pastEventCount = pastDays.reduce((n, d) => n + byDate[d].length, 0);

  const renderDay = (d, isPast) => (
    <div key={d} className="space-y-2">
      <div className={`text-xs uppercase tracking-widest ${isPast ? "text-ink-400" : "text-ink-500"}`}>
        {prettyDate(d)}
      </div>
      <div className="space-y-2">
        {byDate[d].map((ev) => (
          <div
            key={ev.id}
            className={`rounded-xl border border-ivory-200 p-3 ${isPast ? "opacity-70" : ""}`}
            data-testid={`list-event-${ev.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className={`font-medium text-sm ${isPast ? "text-ink-600" : ""}`}>{ev.title}</div>
                <div className="text-xs text-ink-500 mt-0.5 flex flex-wrap gap-2.5">
                  <span>{fmtRange(ev.start_time, ev.end_time)}</span>
                  {ev.venue && <span>{ev.venue}</span>}
                </div>
              </div>
              <span className={`pill !text-[10px] ${IMPACT_PILL[ev.availability_impact] || IMPACT_PILL.unavailable}`}>
                {(ev.availability_impact || "unavailable").replace(/^./, (c) => c.toUpperCase())}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (upcomingDays.length === 0 && pastEventCount === 0) {
    return (
      <div className="card-base p-6 text-center" data-testid="list-empty">
        <AlertCircle size={20} className="mx-auto text-ink-400 mb-2" />
        <p className="text-ink-600 text-sm">This range is empty. Add an event from the Month tab — or block a range for a tour.</p>
      </div>
    );
  }

  return (
    <div className="card-base p-4 sm:p-5 space-y-5" data-testid="upcoming-list">
      {upcomingDays.length === 0 && pastEventCount > 0 && (
        <div className="text-sm text-ink-500 text-center py-2">
          No upcoming events in this range.
        </div>
      )}
      {upcomingDays.map((d) => renderDay(d, false))}

      {pastEventCount > 0 && (
        <div className="pt-2 border-t border-ivory-200">
          <button
            type="button"
            onClick={() => setShowPast((v) => !v)}
            className="w-full flex items-center justify-between text-sm text-ink-600 hover:text-sage-600 py-2"
            data-testid="toggle-past-events"
          >
            <span className="inline-flex items-center gap-2">
              <span className="label-overline">Previous events</span>
              <span className="text-xs text-ink-400">({pastEventCount})</span>
            </span>
            <span aria-hidden className="text-ink-400">{showPast ? "▾" : "▸"}</span>
          </button>
          {showPast && (
            <div className="space-y-5 pt-2" data-testid="past-events-list">
              {pastDays.map((d) => renderDay(d, true))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------- Event create/edit modal ----------------

// Weekday map used by the recurrence picker. Server tokens are 2-letter uppercase.
const WEEKDAY_TOKENS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const REPEAT_PRESETS = [
  { v: "none",     label: "Does not repeat" },
  { v: "daily",    label: "Daily" },
  { v: "weekly",   label: "Weekly" },
  { v: "biweekly", label: "Every 2 weeks" },
  { v: "monthly",  label: "Monthly" },
  { v: "custom",   label: "Custom" },
];

function buildRecurrence(preset, custom, dateStr) {
  // dateStr is the DTSTART used to default byweekday when needed.
  const dow = dateStr ? new Date(dateStr + "T00:00:00").getDay() : 3; // 0=Sun
  const jsDayToToken = (js) => WEEKDAY_TOKENS[(js + 6) % 7];
  const thisWeekday = jsDayToToken(dow);
  switch (preset) {
    case "none": return null;
    case "daily":
      return { freq: "DAILY", interval: 1, count: (custom.count || 30) };
    case "weekly":
      return { freq: "WEEKLY", interval: 1, byweekday: [thisWeekday],
               ...(custom.until ? { until: custom.until }
                                : { count: custom.count || 12 }) };
    case "biweekly":
      return { freq: "WEEKLY", interval: 2, byweekday: [thisWeekday],
               ...(custom.until ? { until: custom.until }
                                : { count: custom.count || 12 }) };
    case "monthly":
      return { freq: "MONTHLY", interval: 1,
               ...(custom.until ? { until: custom.until }
                                : { count: custom.count || 12 }) };
    case "custom": {
      const days = custom.byweekday && custom.byweekday.length
        ? custom.byweekday : [thisWeekday];
      const rec = { freq: "WEEKLY", interval: Math.max(1, Number(custom.interval || 1)),
                    byweekday: days };
      if (custom.until) rec.until = custom.until;
      else rec.count = Math.max(1, Number(custom.count || 12));
      return rec;
    }
    default: return null;
  }
}

function EventModal({ initial, defaultDate, onClose, onSaved }) {
  const isEdit = !!initial;
  const isAuto = isEdit && initial.source_type && initial.source_type !== "manual";
  const isRecurring = !!(initial?.series_id || initial?.is_recurring_instance || initial?.is_series_master);
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    event_type: initial?.event_type || "rehearsal",
    organisation: initial?.organisation || "",
    venue: initial?.venue || "",
    date: initial?.date || defaultDate,
    start_time: initial?.start_time || "",
    end_time: initial?.end_time || "",
    notes: initial?.notes || "",
    availability_impact: initial?.availability_impact || "unavailable",
  }));
  const [allDay, setAllDay] = useState(() => !(initial?.start_time || initial?.end_time));
  const [busy, setBusy] = useState(false);
  // Repeat state — only relevant for new (create) events. Editing a series
  // uses the scope dialog to preserve the existing rule.
  const [repeatPreset, setRepeatPreset] = useState("none");
  const [repeatCustom, setRepeatCustom] = useState({
    interval: 1,
    byweekday: [],
    until: "",
    count: 12,
    endMode: "count", // "count" or "until"
  });
  // Scope for editing a recurring series
  const [editScope, setEditScope] = useState("this");

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      toast.error("Please give the event a title");
      return;
    }
    if (!form.date) {
      toast.error("Date is required");
      return;
    }
    if (!allDay && form.start_time && form.end_time && form.end_time < form.start_time) {
      toast.error("End time must be after start time");
      return;
    }
    setBusy(true);
    const payload = {
      ...form,
      title: form.title.trim().slice(0, 200),
      organisation: (form.organisation || "").trim(),
      venue: (form.venue || "").trim(),
      notes: (form.notes || "").trim(),
      start_time: allDay ? null : (form.start_time || null),
      end_time: allDay ? null : (form.end_time || null),
    };
    // New event: attach recurrence rule from the picker.
    if (!isEdit && repeatPreset !== "none") {
      const custom = { ...repeatCustom };
      if (custom.endMode === "count") { custom.until = ""; }
      else { custom.count = null; }
      payload.recurrence = buildRecurrence(repeatPreset, custom, payload.date);
    }
    try {
      if (isEdit) {
        const scopeParam = isRecurring ? `?scope=${editScope}` : "";
        await api.put(`/diary/events/${initial.id}${scopeParam}`, payload);
        toast.success("Diary event updated");
      } else {
        await api.post("/diary/events", payload);
        toast.success(repeatPreset === "none"
                      ? "Added to your diary"
                      : "Recurring series added to your diary");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2"
      onClick={onClose}
      data-testid="event-modal"
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 shadow-card max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-overline">{isEdit ? "Edit event" : "New event"}</div>
            <h3 className="font-heading text-xl font-semibold mt-1">
              {isEdit ? initial.title || "Event" : "Add diary event"}
            </h3>
          </div>
          <button onClick={onClose} className="btn-ghost" aria-label="Close" data-testid="event-modal-close">
            <X size={18} />
          </button>
        </div>

        {isAuto && (
          <div className="mb-4 rounded-xl bg-ivory-100 p-3 text-xs text-ink-600 inline-flex items-start gap-2">
            <HelpCircle size={14} className="mt-0.5 shrink-0" />
            <span>
              Auto-added to your diary — {sourceLabel(initial.source_type)}. Your
              edits stay on the diary; the original booking/dep isn't changed.
            </span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label-overline block mb-1.5">Title</label>
            <input
              className="input-field"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Westminster Phil Rehearsal"
              data-testid="event-title-input"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-1.5">Event type</label>
              <select
                className="input-field"
                value={form.event_type}
                onChange={(e) => setForm({ ...form, event_type: e.target.value })}
                data-testid="event-type-select"
              >
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{eventTypeLabel(t)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-1.5">Date</label>
              <input
                type="date"
                className="input-field"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                data-testid="event-date-input"
              />
            </div>
          </div>

          <div>
            <label className="label-overline block mb-1.5">Organisation / ensemble</label>
            <input
              className="input-field"
              value={form.organisation}
              onChange={(e) => setForm({ ...form, organisation: e.target.value })}
              placeholder="e.g. Westminster Philharmonic"
              data-testid="event-organisation-input"
            />
          </div>

          <div>
            <label className="label-overline block mb-1.5">Venue</label>
            <input
              className="input-field"
              value={form.venue}
              onChange={(e) => setForm({ ...form, venue: e.target.value })}
              placeholder="e.g. King Solomon Academy"
              data-testid="event-venue-input"
            />
          </div>

          <div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
                className="w-4 h-4 accent-sage-500"
                data-testid="event-allday-toggle"
              />
              <span>All day</span>
            </label>
          </div>

          {!allDay && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-overline block mb-1.5">Start</label>
                <input
                  type="time"
                  className="input-field"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  data-testid="event-start-input"
                />
              </div>
              <div>
                <label className="label-overline block mb-1.5">End</label>
                <input
                  type="time"
                  className="input-field"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  data-testid="event-end-input"
                />
              </div>
            </div>
          )}

          <div>
            <label className="label-overline block mb-1.5">Notes</label>
            <textarea
              className="input-field min-h-[64px]"
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything you'll want to remember on the day"
              data-testid="event-notes-input"
            />
          </div>

          <div>
            <label className="label-overline block mb-1.5">Availability impact</label>
            <div className="grid grid-cols-3 gap-2">
              {IMPACT_OPTIONS.map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm({ ...form, availability_impact: opt.v })}
                  className={`rounded-full py-2 text-sm font-medium ${
                    form.availability_impact === opt.v ? opt.className : "bg-ivory-100 text-ink-700"
                  }`}
                  data-testid={`event-impact-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Recurrence: only offered on create. Editing a series uses the
              scope dialog (below) instead. */}
          {!isEdit && (
            <div className="space-y-2 pt-1" data-testid="event-repeat-block">
              <label className="label-overline block">Repeat</label>
              <select
                className="input-field"
                value={repeatPreset}
                onChange={(e) => setRepeatPreset(e.target.value)}
                data-testid="event-repeat-preset"
              >
                {REPEAT_PRESETS.map((p) => (
                  <option key={p.v} value={p.v}>{p.label}</option>
                ))}
              </select>
              {repeatPreset === "custom" && (
                <div className="rounded-xl border border-ivory-200 p-3 space-y-3" data-testid="event-repeat-custom">
                  <div className="flex items-center gap-2 text-sm">
                    <span>Every</span>
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={repeatCustom.interval}
                      onChange={(e) => setRepeatCustom({ ...repeatCustom, interval: Number(e.target.value) })}
                      className="input-field !w-20 !py-1.5"
                      data-testid="event-repeat-interval"
                    />
                    <span>week(s) on</span>
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {WEEKDAY_LABELS.map((label, i) => {
                      const token = WEEKDAY_TOKENS[i];
                      const on = (repeatCustom.byweekday || []).includes(token);
                      return (
                        <button
                          key={token}
                          type="button"
                          onClick={() => {
                            const cur = new Set(repeatCustom.byweekday || []);
                            if (on) cur.delete(token); else cur.add(token);
                            setRepeatCustom({ ...repeatCustom, byweekday: Array.from(cur) });
                          }}
                          className={`rounded-lg py-1.5 text-xs ${on ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`}
                          data-testid={`event-repeat-day-${token}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {repeatPreset !== "none" && (
                <div className="rounded-xl border border-ivory-200 p-3 space-y-2" data-testid="event-repeat-end">
                  <div className="label-overline">Ends</div>
                  <div className="flex gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="radio"
                        checked={repeatCustom.endMode === "count"}
                        onChange={() => setRepeatCustom({ ...repeatCustom, endMode: "count" })}
                        data-testid="event-repeat-end-count-radio"
                      />
                      <span>after</span>
                      <input
                        type="number"
                        min={1}
                        max={730}
                        value={repeatCustom.count || 12}
                        onChange={(e) => setRepeatCustom({ ...repeatCustom, count: Number(e.target.value), endMode: "count" })}
                        className="input-field !w-20 !py-1"
                        data-testid="event-repeat-count"
                      />
                      <span>occurrence(s)</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="radio"
                        checked={repeatCustom.endMode === "until"}
                        onChange={() => setRepeatCustom({ ...repeatCustom, endMode: "until" })}
                        data-testid="event-repeat-end-until-radio"
                      />
                      <span>on</span>
                      <input
                        type="date"
                        value={repeatCustom.until}
                        onChange={(e) => setRepeatCustom({ ...repeatCustom, until: e.target.value, endMode: "until" })}
                        className="input-field !py-1"
                        data-testid="event-repeat-until"
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Editing an occurrence of a series — offer scope picker. */}
          {isEdit && isRecurring && (
            <div className="space-y-2 pt-1" data-testid="event-edit-scope-block">
              <label className="label-overline block">Apply this change to</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { v: "this", label: "This event only" },
                  { v: "this_and_future", label: "This and future" },
                  { v: "series", label: "Entire series" },
                ].map((s) => (
                  <button
                    key={s.v}
                    type="button"
                    onClick={() => setEditScope(s.v)}
                    className={`rounded-lg py-2 text-xs font-medium ${editScope === s.v ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`}
                    data-testid={`event-edit-scope-${s.v}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full justify-center"
            data-testid="event-submit-btn"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Add event"}
          </button>
        </form>
      </div>
    </div>
  );
}


// ---------------- Legacy bulk availability helpers (kept for power-users) ----------------

function RepeatModal({ onClose, startDate }) {
  const [weekday, setWeekday] = useState(new Date(startDate + "T00:00:00").getDay() === 0 ? 6 : new Date(startDate + "T00:00:00").getDay() - 1);
  const [weeks, setWeeks] = useState(8);
  const [slot, setSlot] = useState("all_day");
  const [status, setStatus] = useState("available");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/availability/repeat", { weekday, weeks, slot, status, start_date: startDate });
      toast.success(`Set ${weeks} ${["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][weekday]}s`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="repeat-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 pb-32 sm:pb-6 shadow-card max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">Repeat weekly pattern</h3>
          <button onClick={onClose} className="btn-ghost" aria-label="Close" data-testid="repeat-close-btn"><X size={18} /></button>
        </div>
        <p className="text-xs text-ink-600 mb-4">Repeat a weekly pattern — e.g. "every Tuesday morning, blocked". Use detailed events from the day view for specific gigs.</p>
        <div className="space-y-4">
          <div>
            <label className="label-overline block mb-2">Weekday</label>
            <div className="grid grid-cols-7 gap-1">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                <button key={d} onClick={() => setWeekday(i)} className={`rounded-xl py-2 text-sm font-medium ${weekday === i ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`} data-testid={`repeat-day-${i}`}>{d}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-2">For</label>
              <select className="input-field" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} data-testid="repeat-weeks-select">
                {[4, 6, 8, 12, 16, 26, 52].map((w) => <option key={w} value={w}>{w} weeks</option>)}
              </select>
            </div>
            <div>
              <label className="label-overline block mb-2">Slot</label>
              <select className="input-field" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="repeat-slot-select">
                <option value="all_day">All day</option>
                <option value="daytime">Daytime</option>
                <option value="evening">Evening</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setStatus("available")} className={`rounded-full py-2 ${status === "available" ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`} data-testid="repeat-status-available">Available</button>
            <button onClick={() => setStatus("unavailable")} className={`rounded-full py-2 ${status === "unavailable" ? "bg-terracotta-500 text-white" : "bg-ivory-100 text-ink-700"}`} data-testid="repeat-status-unavailable">Unavailable</button>
          </div>
          <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center" data-testid="repeat-submit-btn">{busy ? "Saving…" : "Save pattern"}</button>
        </div>
      </div>
    </div>
  );
}


function RangeModal({ onClose, startDate }) {
  const [start, setStart] = useState(startDate);
  const [end, setEnd] = useState(startDate);
  const [status, setStatus] = useState("available");
  const [slot, setSlot] = useState("all_day");
  const [customStart, setCustomStart] = useState("19:00");
  const [customEnd, setCustomEnd] = useState("22:00");
  const [days, setDays] = useState([0, 1, 2, 3, 4, 5, 6]);
  const [busy, setBusy] = useState(false);

  const toggleDay = (i) => setDays(days.includes(i) ? days.filter((d) => d !== i) : [...days, i].sort());

  const submit = async () => {
    if (!start || !end) { toast.error("Pick start and end dates"); return; }
    if (end < start) { toast.error("End date must be after start date"); return; }
    setBusy(true);
    try {
      const body = {
        start_date: start, end_date: end, status, slot,
        weekdays: days.length === 7 ? [] : days,
      };
      if (slot === "custom") { body.custom_start = customStart; body.custom_end = customEnd; }
      const { data } = await api.post("/availability/range", body);
      toast.success(`Saved ${data.count} day${data.count === 1 ? "" : "s"} to your diary`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/40 p-2" onClick={onClose} data-testid="range-modal">
      <div className="bg-white w-full max-w-md rounded-2xl sm:rounded-3xl p-6 pb-32 sm:pb-6 shadow-card max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl font-semibold">Block a date range</h3>
          <button onClick={onClose} className="btn-ghost" aria-label="Close" data-testid="range-close-btn"><X size={18} /></button>
        </div>
        <p className="text-xs text-ink-600 mb-4">Block (or open) a date range in one go — perfect for tours and holidays. Use day-by-day events for individual gigs.</p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-overline block mb-2">From</label>
              <input type="date" className="input-field" value={start} onChange={(e) => setStart(e.target.value)} data-testid="range-start-input" />
            </div>
            <div>
              <label className="label-overline block mb-2">To</label>
              <input type="date" className="input-field" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="range-end-input" />
            </div>
          </div>
          <div>
            <label className="label-overline block mb-2">Status</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "available", label: "Available", cls: "bg-sage-500 text-white" },
                { v: "maybe", label: "Maybe", cls: "bg-amber-deep text-white" },
                { v: "unavailable", label: "Unavailable", cls: "bg-terracotta-500 text-white" },
              ].map((s) => (
                <button key={s.v} type="button" onClick={() => setStatus(s.v)} className={`rounded-full py-2 text-sm font-medium ${status === s.v ? s.cls : "bg-ivory-100 text-ink-700"}`} data-testid={`range-status-${s.v}`}>{s.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="label-overline block mb-2">When</label>
            <select className="input-field" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="range-slot-select">
              <option value="all_day">All day</option>
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="daytime">Daytime (full)</option>
              <option value="evening">Evening</option>
              <option value="custom">Custom times…</option>
            </select>
          </div>
          {slot === "custom" && (
            <div className="grid grid-cols-2 gap-3" data-testid="range-custom-times">
              <div>
                <label className="label-overline block mb-2">Start</label>
                <input type="time" className="input-field" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              </div>
              <div>
                <label className="label-overline block mb-2">End</label>
                <input type="time" className="input-field" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </div>
            </div>
          )}
          <div>
            <label className="label-overline block mb-2">Only on these weekdays</label>
            <div className="grid grid-cols-7 gap-1">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                <button key={d} type="button" onClick={() => toggleDay(i)} className={`rounded-xl py-2 text-sm font-medium ${days.includes(i) ? "bg-sage-500 text-white" : "bg-ivory-100 text-ink-700"}`} data-testid={`range-day-${i}`}>{d}</button>
              ))}
            </div>
            <p className="text-xs text-ink-500 mt-1">Tip: tap to toggle. All selected = every day in the range.</p>
          </div>
          <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center" data-testid="range-submit-btn">{busy ? "Saving…" : "Save range"}</button>
        </div>
      </div>
    </div>
  );
}


// ---------------- Scope picker (edit / delete a recurring event) ----------------

function ScopeDialog({ mode, event, onCancel, onConfirm }) {
  const isDelete = mode === "delete";
  const [scope, setScope] = useState("this");
  const options = [
    { v: "this", label: "This event only" },
    { v: "this_and_future", label: "This and future events" },
    { v: "series", label: "Entire series" },
  ];
  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink-900/50 p-2"
      onClick={onCancel}
      data-testid="scope-dialog"
    >
      <div
        className="bg-white w-full max-w-sm rounded-2xl sm:rounded-3xl p-6 shadow-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold">
            {isDelete ? "Delete recurring event" : "Edit recurring event"}
          </h3>
          <button onClick={onCancel} className="btn-ghost" aria-label="Close" data-testid="scope-dialog-close">
            <X size={18} />
          </button>
        </div>
        <p className="text-sm text-ink-600 mb-4">
          {event?.title || "This event"} repeats. Choose what to {isDelete ? "delete" : "change"}.
        </p>
        <div className="space-y-2 mb-4">
          {options.map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => setScope(o.v)}
              className={`w-full rounded-xl py-2.5 text-sm font-medium border transition ${scope === o.v ? "bg-sage-500 text-white border-sage-500" : "bg-white text-ink-700 border-ivory-200 hover:bg-ivory-50"}`}
              data-testid={`scope-dialog-${o.v}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary flex-1 justify-center" data-testid="scope-dialog-cancel">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(scope)}
            className={`flex-1 justify-center rounded-full py-2.5 font-semibold text-sm ${isDelete ? "bg-terracotta-500 text-white" : "bg-sage-500 text-white"}`}
            data-testid="scope-dialog-confirm"
          >
            {isDelete ? "Delete" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
